/* eslint-disable */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertCircle, Plus, Edit, Search, X, Check, Save,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { useLang } from "../../context/LangContext";
import { API, useFmt, CheckCircle2BigIcon } from "./_shared";
import RejectOrderDialog from "../../components/RejectOrderDialog";

const AgentOrderEditDialog = ({ order, onClose, onSaved }) => {
  const fm = useFmt();
  const { t } = useLang();
  const [items, setItems] = useState(order.items.map((it) => ({ ...it })));
  const [deliveryAddress, setDeliveryAddress] = useState(order.delivery_address || { label: "", address: "", city: "", cap: "", province: "", notes: "" });
  const [deliveryNotes, setDeliveryNotes] = useState(order.delivery_notes || "");
  const [specialRequests, setSpecialRequests] = useState(order.customer?.special_requests || "");
  const [saving, setSaving] = useState(false);
  const isForeign = !!order.customer?.is_foreign;

  // Product catalog (for adding new items) — loaded lazily on first search
  const [catalog, setCatalog] = useState([]);
  const [searchProd, setSearchProd] = useState("");
  const [showCatalog, setShowCatalog] = useState(false);

  useEffect(() => {
    if (catalog.length === 0) {
      axios.get(`${API}/products`).then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setCatalog(list.filter((p) => p.is_active));
      }).catch(() => {});
    }
  }, [catalog.length]);

  const updateItem = (idx, field, value) => {
    const arr = [...items];
    arr[idx] = { ...arr[idx], [field]: value };
    arr[idx].line_total = +((arr[idx].quantity || 0) * (arr[idx].unit_price || 0)).toFixed(2);
    setItems(arr);
  };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const addItem = (prod) => {
    const pricelistId = order.pricelist_type_id;
    const price = (prod.prices && pricelistId) ? (prod.prices[pricelistId] || 0) : (prod.base_price || 0);
    setItems([...items, {
      product_id: prod.id,
      code: prod.code,
      description: prod.description,
      quantity: 1,
      unit: prod.unit || "Kg",
      unit_price: price,
      line_total: price,
      is_free: false,
      vat_rate: prod.vat_rate || 0.10,
      note: "",
    }]);
    setSearchProd("");
    setShowCatalog(false);
    toast.success(`${t.added || "Aggiunto"}: ${prod.code}`);
  };

  const filteredCatalog = searchProd.trim()
    ? catalog.filter((p) =>
        (p.code || "").toLowerCase().includes(searchProd.toLowerCase()) ||
        (p.description || "").toLowerCase().includes(searchProd.toLowerCase())
      ).slice(0, 12)
    : catalog.slice(0, 12);

  const subtotal = items.reduce((s, it) => s + (it.line_total || 0), 0);
  const vatTotal = isForeign ? 0 : items.reduce((s, it) => s + ((it.line_total || 0) * (it.vat_rate || 0)), 0);
  const grandTotal = subtotal + vatTotal;

  const save = async (alsoApprove) => {
    if (items.some((it) => !it.description || (it.quantity || 0) <= 0)) { toast.error("Quantità e descrizione obbligatorie"); return; }
    setSaving(true);
    try {
      await axios.patch(`${API}/orders/${order.id}/agent-edit`, {
        items: items.map((it) => ({
          product_id: it.product_id || null,
          code: it.code,
          description: it.description,
          quantity: parseFloat(it.quantity) || 0,
          unit: it.unit || "Kg",
          unit_price: parseFloat(it.unit_price) || 0,
          line_total: +((parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0)).toFixed(2),
          is_free: !!it.is_free,
          vat_rate: parseFloat(it.vat_rate) || 0.10,
          note: it.note || "",
        })),
        delivery_address: deliveryAddress,
        delivery_notes: deliveryNotes,
        special_requests: specialRequests,
      });
      if (alsoApprove) {
        await axios.post(`${API}/orders/${order.id}/agent-approve`);
        toast.success("Modifiche salvate e ordine approvato");
      } else {
        toast.success(t.pAgent_loginCreated);
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-white max-h-[95vh] overflow-y-auto" data-testid="agent-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Edit className="w-5 h-5 text-[#8A1C2A]" /> Rivedi ordine {order.order_number}</DialogTitle>
        </DialogHeader>
        <Card className="bg-stone-50 border-stone-200 p-3 text-sm">
          <p className="font-semibold">{order.customer?.customer_name}</p>
          <p className="text-xs text-stone-600">{order.customer?.email} · {order.customer?.phone}</p>
        </Card>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold">{t.items || "Articoli"}</Label>
            <Button size="sm" variant="outline" className="border-[#C5A059] text-[#8A1C2A] h-8" onClick={() => setShowCatalog(!showCatalog)} data-testid="toggle-add-product-btn">
              <Plus className="w-3 h-3 mr-1" /> {t.addProduct || "Aggiungi prodotto"}
            </Button>
          </div>
          {showCatalog && (
            <div className="bg-stone-50 border border-stone-200 rounded-md p-3 space-y-2">
              <Input
                value={searchProd}
                onChange={(e) => setSearchProd(e.target.value)}
                placeholder={t.searchProduct || "Cerca per codice o descrizione..."}
                className="bg-white border-stone-300 h-8 text-sm"
                data-testid="add-product-search"
                autoFocus
              />
              <div className="max-h-48 overflow-y-auto space-y-1">
                {filteredCatalog.length === 0 && <p className="text-xs text-stone-400 italic text-center py-2">{t.noProductsFound || "Nessun prodotto"}</p>}
                {filteredCatalog.map((p) => {
                  const price = (p.prices && order.pricelist_type_id) ? (p.prices[order.pricelist_type_id] || 0) : (p.base_price || 0);
                  return (
                    <button
                      key={p.id}
                      onClick={() => addItem(p)}
                      className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-xs bg-white border border-stone-200 hover:border-[#8A1C2A] hover:bg-[#FAF8F5] rounded text-left transition-colors"
                      data-testid={`add-product-${p.code}`}
                    >
                      <span className="font-mono text-stone-500 w-16 shrink-0">{p.code}</span>
                      <span className="flex-1 truncate">{p.description}</span>
                      <span className="font-semibold text-[#8A1C2A] shrink-0">{fm(price)}/{p.unit || "Kg"}</span>
                      <Plus className="w-3 h-3 text-[#8A1C2A] shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="bg-stone-100">
                <tr>
                  <th className="text-left p-2 text-xs">Codice</th>
                  <th className="text-left p-2 text-xs">Descrizione</th>
                  <th className="text-right p-2 text-xs w-20">Q.tà</th>
                  <th className="text-right p-2 text-xs w-20">UM</th>
                  <th className="text-right p-2 text-xs w-24">€/UM</th>
                  <th className="text-right p-2 text-xs w-24">Tot.</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx} className="border-t border-stone-100" data-testid={`edit-item-${idx}`}>
                    <td className="p-1 font-mono text-xs">{it.code}</td>
                    <td className="p-1"><Input value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} className="bg-white border-stone-300 h-8 text-xs" /></td>
                    <td className="p-1"><Input type="number" step="0.1" min="0" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", parseFloat(e.target.value) || 0)} className="bg-white border-stone-300 h-8 text-xs text-right" /></td>
                    <td className="p-1 text-xs text-center">{it.unit}</td>
                    <td className="p-1"><Input type="number" step="0.01" min="0" value={it.unit_price} onChange={(e) => updateItem(idx, "unit_price", parseFloat(e.target.value) || 0)} className="bg-white border-stone-300 h-8 text-xs text-right" /></td>
                    <td className="p-1 text-right text-xs font-semibold text-[#8A1C2A]">{(it.line_total || 0).toFixed(2)}</td>
                    <td className="p-1 text-center"><Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeItem(idx)}><X className="w-3 h-3 text-red-500" /></Button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-stone-50 border-t-2 border-stone-200"><td colSpan="5" className="p-2 text-right font-semibold">Imponibile</td><td className="p-2 text-right font-semibold">{fm(subtotal)}</td><td></td></tr>
                <tr><td colSpan="5" className="px-2 py-1 text-right text-xs">IVA {isForeign ? "(estero 0%)" : ""}</td><td className="px-2 py-1 text-right text-xs">{fm(vatTotal)}</td><td></td></tr>
                <tr className="border-t border-stone-200"><td colSpan="5" className="p-2 text-right font-bold">Totale</td><td className="p-2 text-right text-lg font-bold text-[#8A1C2A]">{fm(grandTotal)}</td><td></td></tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="border-t border-stone-200 pt-3">
          <Label className="text-xs font-semibold">Indirizzo di consegna</Label>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mt-1">
            <div className="md:col-span-3"><Input value={deliveryAddress.label || ""} onChange={(e) => setDeliveryAddress({ ...deliveryAddress, label: e.target.value })} placeholder="Etichetta" className="bg-white border-stone-300 h-9" /></div>
            <div className="md:col-span-5"><Input value={deliveryAddress.address || ""} onChange={(e) => setDeliveryAddress({ ...deliveryAddress, address: e.target.value })} placeholder="Indirizzo" className="bg-white border-stone-300 h-9" /></div>
            <div className="md:col-span-2"><Input value={deliveryAddress.city || ""} onChange={(e) => setDeliveryAddress({ ...deliveryAddress, city: e.target.value })} placeholder="Città" className="bg-white border-stone-300 h-9" /></div>
            <div className="md:col-span-1"><Input value={deliveryAddress.cap || ""} onChange={(e) => setDeliveryAddress({ ...deliveryAddress, cap: e.target.value })} placeholder="CAP" className="bg-white border-stone-300 h-9" /></div>
            <div className="md:col-span-1"><Input value={deliveryAddress.province || ""} onChange={(e) => setDeliveryAddress({ ...deliveryAddress, province: e.target.value })} placeholder="PR" className="bg-white border-stone-300 h-9" /></div>
            <div className="md:col-span-12"><Input value={deliveryAddress.notes || ""} onChange={(e) => setDeliveryAddress({ ...deliveryAddress, notes: e.target.value })} placeholder="Note indirizzo (orari, citofono...)" className="bg-white border-stone-300 h-9" /></div>
          </div>
          <Label className="text-xs mt-2 block">Note ordine</Label>
          <Input value={deliveryNotes} onChange={(e) => setDeliveryNotes(e.target.value)} placeholder="Note specifiche per questo ordine" className="bg-white border-stone-300" />
          <Label className="text-xs mt-2 block">Richieste speciali del cliente</Label>
          <textarea value={specialRequests} onChange={(e) => setSpecialRequests(e.target.value)} rows={2} className="w-full mt-1 px-3 py-2 text-sm bg-white border border-stone-300 rounded-md" />
        </div>

        <DialogFooter className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button>
          <Button onClick={() => save(false)} disabled={saving} variant="outline" className="border-[#C5A059] text-[#8A1C2A]" data-testid="save-edit-btn">
            <Save className="w-4 h-4 mr-1" /> Salva modifiche
          </Button>
          <Button onClick={() => save(true)} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="save-and-approve-btn">
            <Check className="w-4 h-4 mr-1" /> Salva e approva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ApprovalsList = ({ onChanged }) => {
  const { t } = useLang();
  const fm = useFmt();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [rejectOrder, setRejectOrder] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/orders?status=pending_agent`);
      setOrders(r.data || []);
    } catch { toast.error(t.pAgent_loadError); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (orderId) => {
    if (!window.confirm("Approvare l'ordine e inviarlo al magazzino?")) return;
    try {
      await axios.post(`${API}/orders/${orderId}/agent-approve`);
      toast.success(t.pAgent_orderApproved);
      load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const reject = (orderId) => {
    const ord = orders.find(o => o.id === orderId);
    setRejectOrder(ord);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900">Ordini da approvare</h1>
          <p className="text-stone-500 text-sm">{orders.length} ordini in attesa della tua revisione</p>
        </div>
      </div>

      <Card className="bg-amber-50 border-amber-200 p-3 mb-4">
        <p className="text-xs text-amber-900"><AlertCircle className="w-4 h-4 inline mr-1" /> Quando un cliente crea un ordine dal proprio portale, lo trovi qui per revisionarlo, modificarlo se necessario e approvarlo prima di inoltrarlo al magazzino.</p>
      </Card>

      {loading ? <div className="p-8 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>
        : orders.length === 0 ? (
          <Card className="bg-white border-stone-200 p-12 text-center">
            <CheckCircle2BigIcon /><p className="text-stone-500 mt-3">Nessun ordine da approvare 👍</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {orders.map((o) => (
              <Card key={o.id} className="bg-white border-amber-200 p-4" data-testid={`approval-card-${o.order_number}`}>
                <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                  <div>
                    <p className="font-mono font-semibold text-[#8A1C2A]">{o.order_number}</p>
                    <p className="text-xs text-stone-500">{new Date(o.created_at).toLocaleString("it-IT")}</p>
                  </div>
                  <Badge className="bg-amber-100 text-amber-800">In revisione agente</Badge>
                </div>
                <div className="mb-2">
                  <p className="font-medium">{o.customer?.customer_name}</p>
                  <p className="text-xs text-stone-500">{o.customer?.email} · {o.customer?.phone}</p>
                </div>
                {o.delivery_address && (
                  <div className="text-xs bg-stone-50 p-2 rounded mb-2">
                    <p>📍 {o.delivery_address.label && <strong>{o.delivery_address.label}: </strong>}{o.delivery_address.address}, {o.delivery_address.city}</p>
                    {o.delivery_address.notes && <p className="italic text-stone-600">{o.delivery_address.notes}</p>}
                    {o.delivery_notes && <p className="italic text-amber-700">📝 {o.delivery_notes}</p>}
                  </div>
                )}
                <div className="text-xs space-y-0.5 max-h-32 overflow-y-auto bg-stone-50 p-2 rounded mb-3">
                  {o.items?.map((it, i) => (
                    <div key={i} className="flex justify-between gap-2">
                      <span className="truncate">{it.description}</span>
                      <span className="text-stone-600 whitespace-nowrap">{it.quantity} {it.unit} × {fm(it.unit_price)}</span>
                    </div>
                  ))}
                </div>
                {o.customer?.special_requests && (
                  <p className="text-xs italic text-yellow-800 bg-yellow-50 p-2 rounded mb-2">💬 {o.customer.special_requests}</p>
                )}
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="font-bold text-[#8A1C2A]">{fm(((o.grand_total ?? o.total_amount) || 0))}</span>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => reject(o.id)} className="border-red-300 text-red-700 hover:bg-red-50" data-testid={`reject-${o.order_number}`}>
                      <X className="w-3 h-3 mr-1" /> Rifiuta
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing(o)} className="border-[#C5A059] text-[#8A1C2A]" data-testid={`edit-approval-${o.order_number}`}>
                      <Edit className="w-3 h-3 mr-1" /> Rivedi
                    </Button>
                    <Button size="sm" onClick={() => approve(o.id)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-${o.order_number}`}>
                      <Check className="w-3 h-3 mr-1" /> Approva
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

      {editing && <AgentOrderEditDialog order={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onChanged && onChanged(); }} />}
      <RejectOrderDialog
        open={!!rejectOrder}
        order={rejectOrder}
        onClose={() => setRejectOrder(null)}
        onRejected={() => { setRejectOrder(null); load(); onChanged && onChanged(); }}
      />
    </div>
  );
};

export default ApprovalsList;
